import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { customerApi } from "@/lib/customerApi";

// The one LightningFlow customer account, shared across every consumer
// product (Halo SuperVPN, SecureShield Pro, and whatever comes next) — not a
// per-product identity. `entitlements` is the durable answer to "which
// products does this customer own," fetched from the backend on every
// refresh rather than inferred from local/client state.
const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null); // null = not loaded yet, false = signed out
  const [entitlements, setEntitlements] = useState([]);
  const [portal, setPortal] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await customerApi.get("/customers/portal");
      setCustomer(data.customer);
      setEntitlements(data.entitlements);
      setPortal(data);
      setOnboarding(null);
    } catch {
      try {
        const { data } = await customerApi.get("/customers/onboarding");
        setOnboarding(data);
        setCustomer(false);
      } catch {
        setCustomer(false);
        setOnboarding(null);
      }
      setEntitlements([]);
      setPortal(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const register = useCallback(async (email, password, name, productOffersConsent = false) => {
    const { data } = await customerApi.post("/customers/register", {
      email, password, name, product_offers_consent: productOffersConsent === true,
    });
    if (data.verification_required) {
      setCustomer(false);
      setEntitlements([]);
      setPortal(null);
      setOnboarding(null);
      return data;
    }
    setCustomer(data.customer);
    await refresh();
    return data;
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await customerApi.post("/customers/login", { email, password });
    setCustomer(data.customer);
    setOnboarding(null);
    await refresh();
    return data.customer;
  }, [refresh]);

  const logout = useCallback(async () => {
    await customerApi.post("/customers/logout");
    setCustomer(false);
    setEntitlements([]);
    setPortal(null);
    setOnboarding(null);
  }, []);

  const hasEntitlement = useCallback(
    (product) => entitlements.some((e) => e.product === product && e.status === "active"),
    [entitlements]
  );

  const value = useMemo(
    () => ({ customer, entitlements, portal, onboarding, ready, register, login, logout, refresh, hasEntitlement }),
    [customer, entitlements, portal, onboarding, ready, register, login, logout, refresh, hasEntitlement]
  );

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomerAuth must be used inside CustomerAuthProvider");
  return ctx;
}
